export type PlanKey = "starter" | "growth" | "elite";

export type Plan = {
  key: PlanKey;
  name: string;
  priceMonthly: number;
  priceLabel?: string;
  minutesIncluded?: number;
  seatsIncluded: number | "unlimited";
  callVolume: string;
  tagline: string;
  bestFor: string[];
  includes: string[];
};

export type AddOn = {
  key: string;
  name: string;
  price: string; // "$97/mo", "$200 one-time", etc.
  description: string;
};

/** Length of the free trial. Must match the trial set on the Stripe payment links. */
export const TRIAL_DAYS = 14;

export const DEMO_BOOKING_URL = "https://cal.com/team/asep/discovery";

export const TRIAL = {
  copy: "Pricing starts at $299 a month and scales with seats, call volume and the capabilities you need. We set up your line with you. Month to month, cancel anytime.",
  roiLine: "One recovered missed call — a single new patient or client — typically covers the month.",
};

export const PLANS: Plan[] = [
  {
    key: "starter",
    name: "Starter",
    priceMonthly: 299,
    seatsIncluded: 1,
    callVolume: "Up to ~200 calls/month",
    tagline: "Inbound receptionist + essential automation",
    bestFor: ["Solo practitioners or small teams", "Small 2–3 person offices", "New businesses"],
    includes: [
      "AI inbound receptionist (Twilio + voice AI)",
      "Basic call routing & voicemail",
      "Business hours + after-hours flows",
      "Call summaries (basic)",
      "Caller memory — remembers every caller for 30 days",
      "Admin portal basics (greetings + voice selection)",
    ],
  },
  {
    key: "growth",
    name: "Growth",
    priceMonthly: 499,
    priceLabel: "Custom",
    seatsIncluded: 5,
    callVolume: "Up to ~500 calls/month",
    tagline: "Knowledge base + long caller memory",
    bestFor: ["Growing businesses", "Teams handling 20–80 calls/day", "Scaling service + sales"],
    includes: [
      "Everything in Starter",
      "Knowledge base + multi-intent receptionist",
      "Caller memory — remembers every caller for a full year",
      "Dynamic greetings for returning callers",
      "Done-for-you conversation scripts",
      "Industry-appropriate greeting templates",
      "Admin portal controls (greetings, voice, call flows)",
    ],
  },
  {
    key: "elite",
    name: "Elite",
    priceMonthly: 899,
    priceLabel: "Custom",
    seatsIncluded: "unlimited",
    callVolume: "Up to ~1,200 calls/month",
    tagline: "Multi-location + done-for-you setup + white-glove onboarding",
    bestFor: ["High-volume offices", "Multi-location", "Serious automation"],
    includes: [
      "Everything in Growth",
      "Multi-location / multi-number support",
      "Dedicated AI voice tuning (ElevenLabs)",
      "Custom AI persona built for your brand",
      "Priority routing for high-value callers",
      "Dedicated AI model for faster, smarter responses",
      "Internal “AI Assistant” mode for staff tasks",
      "Caller memory — extended retention + document knowledge",
      "Dedicated onboarding specialist",
    ],
  },
];

export const ENTERPRISE = {
  name: "Enterprise / Multi-Location",
  priceRange: "$24k–$84k/year per region",
  includes: [
    "White-label NeuroSphere AI",
    "Shared knowledge base ingestion",
    "High-volume call handling mode",
    "Dedicated API + engineering support",
    "Training for 50–500 locations or teams",
    "Priority voice + industry tuning",
  ],
};

export const ADDONS: AddOn[] = [
  {
    key: "custom_voice",
    name: "Custom Voice / Personality",
    price: "$200 one-time",
    description: "Custom ElevenLabs tuning for your brand voice",
  },
];
